import React from "react";
import * as Mui from "@mui/material";
import { makeStyles } from "tss-react/mui";
import * as Icons from "react-feather";
import { Dialog } from "component/shared/dialog";

const useStyles = makeStyles()((theme) => ({
	title: {
		borderBottom: `1px solid ${theme.palette.divider}`,
	},
	titleText: {
		fontSize: 18,
		fontWeight: 600,
	},
	intro: {
		color: theme.palette.custom.textPrimary,
		marginTop: 16,
		marginBottom: 16,
	},
	panel: {
		backgroundColor: theme.palette.action.hover,
		borderRadius: 4,
		padding: 12,
	},
	checkbox: {
		padding: 0,
	},
}));

interface Props {
	open: boolean;
	onClose: () => void;
	title: string;
	/** Explains what the preview represents. Differs between the two disclaimer types. */
	intro: string;
	/** The consent checkbox wording exactly as it will render on lead capture forms. */
	disclaimerText: string;
	/**
	 * Label for the standard disclaimer's second consent checkbox, shown as its own panel. Omitted
	 * for the Follow Up Boss disclaimer, which suppresses that checkbox, and omitted for the
	 * standard disclaimer when neither URL is configured.
	 */
	privacyConsentText?: string;
	/** Rendered as a link when set, and as plain text when not — matching the forms. */
	privacyPolicyUrl?: string;
	/** True for the Follow Up Boss variant, which appends the Privacy Policy inline. */
	showInlinePrivacyPolicy?: boolean;
}

/**
 * Read-only preview of the disclaimer wording a visitor will see. Agents cannot edit the text —
 * they only choose which variant applies.
 */
const Component: React.FC<Props> = (props) => {
	const {
		open,
		onClose,
		title,
		intro,
		disclaimerText,
		privacyConsentText,
		privacyPolicyUrl,
		showInlinePrivacyPolicy,
	} = props;
	const { classes } = useStyles();
	const privacyPolicyLabel = "Privacy Policy";
	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<Mui.DialogTitle className={classes.title}>
				<Mui.Grid container justifyContent="space-between" alignItems="center" wrap="nowrap">
					<Mui.Grid item>
						<Mui.Typography className={classes.titleText}>{title}</Mui.Typography>
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.IconButton size="small" aria-label="Close" onClick={onClose}>
							<Icons.X size={20} />
						</Mui.IconButton>
					</Mui.Grid>
				</Mui.Grid>
			</Mui.DialogTitle>
			<Mui.DialogContent>
				<Mui.Typography variant="body2" className={classes.intro}>
					{intro}
				</Mui.Typography>
				<Mui.Box className={classes.panel}>
					<Mui.Grid container wrap="nowrap" spacing={1}>
						<Mui.Grid item>
							<Mui.Checkbox disabled checked={false} size="small" className={classes.checkbox} />
						</Mui.Grid>
						<Mui.Grid item>
							<Mui.Typography variant="body2">
								{disclaimerText}
								{showInlinePrivacyPolicy && (
									<>
										{" "}
										{privacyPolicyUrl ? (
											<Mui.Link href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
												{privacyPolicyLabel}
											</Mui.Link>
										) : (
											privacyPolicyLabel
										)}
									</>
								)}
							</Mui.Typography>
						</Mui.Grid>
					</Mui.Grid>
				</Mui.Box>
				{privacyConsentText && (
					<Mui.Box className={classes.panel} mt={1}>
						<Mui.Grid container wrap="nowrap" spacing={1}>
							<Mui.Grid item>
								<Mui.Checkbox disabled checked={false} size="small" className={classes.checkbox} />
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.Typography variant="body2">{privacyConsentText}</Mui.Typography>
							</Mui.Grid>
						</Mui.Grid>
					</Mui.Box>
				)}
			</Mui.DialogContent>
		</Dialog>
	);
};

export const DisclaimerPreviewDialog = Component;
